import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchFamilles, deleteFamille } from '@/store/thunks/familleThunks';
import { fetchNatures, deleteNature } from '@/store/thunks/natureThunks';
import type { Famille } from '@/types';
import type { Nature } from '@/types';
import { filterListByQuery } from '@/utils/filterList';
import { AddButton, FilterInput, DataTable, Modal, ConfirmModal, ListWithPagination, TableActions } from '@/components/ui';
import FamilleForm from './FamilleForm';
import NatureForm from './NatureForm';

const PAGE_SIZE = 10;

export default function EnginFamilleTab() {
  const dispatch = useAppDispatch();
  const familles = useAppSelector((s: RootState) => s.famille.familles);
  const natures = useAppSelector((s: RootState) => s.nature.natures);
  const [familleModalOpen, setFamilleModalOpen] = useState(false);
  const [natureModalOpen, setNatureModalOpen] = useState(false);
  const [editingFamille, setEditingFamille] = useState<Famille | null>(null);
  const [editingNature, setEditingNature] = useState<Nature | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Famille | Nature | null>(null);
  const [confirmType, setConfirmType] = useState<'famille' | 'nature'>('famille');
  const [famillePage, setFamillePage] = useState(0);
  const [naturePage, setNaturePage] = useState(0);
  const [familleFilter, setFamilleFilter] = useState('');
  const [natureFilter, setNatureFilter] = useState('');

  const filteredFamilles = filterListByQuery(familles, familleFilter, ['nom']);
  const filteredNatures = filterListByQuery(natures, natureFilter, ['nom']);

  const familleTotalPages = Math.max(1, Math.ceil(filteredFamilles.length / PAGE_SIZE));
  const familleEffectivePage = Math.min(famillePage, familleTotalPages - 1);
  const slicedFamilles = filteredFamilles.slice(familleEffectivePage * PAGE_SIZE, (familleEffectivePage + 1) * PAGE_SIZE);

  const natureTotalPages = Math.max(1, Math.ceil(filteredNatures.length / PAGE_SIZE));
  const natureEffectivePage = Math.min(naturePage, natureTotalPages - 1);
  const slicedNatures = filteredNatures.slice(natureEffectivePage * PAGE_SIZE, (natureEffectivePage + 1) * PAGE_SIZE);

  useEffect(() => {
    dispatch(fetchFamilles());
    dispatch(fetchNatures());
  }, [dispatch]);

  useEffect(() => {
    setFamillePage((p) => Math.min(p, familleTotalPages - 1));
  }, [familleTotalPages]);

  useEffect(() => {
    setNaturePage((p) => Math.min(p, natureTotalPages - 1));
  }, [natureTotalPages]);

  const handleDelete = () => {
    if (!toDelete) return;
    if (confirmType === 'famille') dispatch(deleteFamille(toDelete as Famille));
    else dispatch(deleteNature(toDelete as Nature));
    setToDelete(null);
    setConfirmOpen(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-medium">Familles</h3>
            <FilterInput value={familleFilter} onChange={setFamilleFilter} placeholder="Rechercher…" id="famille-filter" />
          </div>
          <AddButton label="Ajouter" onClick={() => { setEditingFamille(null); setFamilleModalOpen(true); }} />
        </div>
        <ListWithPagination
          page={familleEffectivePage}
          totalPages={familleTotalPages}
          onPageChange={setFamillePage}
        >
          <DataTable<Famille>
            columns={[{ key: 'id', label: 'Id' }, { key: 'nom', label: 'Nom' }]}
            data={slicedFamilles}
            keyField="id"
            emptyMessage="Aucune famille"
            actions={(row) => (
              <TableActions
                onEdit={() => { setEditingFamille(row); setFamilleModalOpen(true); }}
                onDelete={() => { setToDelete(row); setConfirmType('famille'); setConfirmOpen(true); }}
              />
            )}
          />
        </ListWithPagination>
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-medium">Natures des travaux</h3>
            <FilterInput value={natureFilter} onChange={setNatureFilter} placeholder="Rechercher…" id="nature-filter" />
          </div>
          <AddButton label="Ajouter" onClick={() => { setEditingNature(null); setNatureModalOpen(true); }} />
        </div>
        <ListWithPagination
          page={natureEffectivePage}
          totalPages={natureTotalPages}
          onPageChange={setNaturePage}
        >
          <DataTable<Nature>
            columns={[
              { key: 'id', label: 'Id' },
              { key: 'nom', label: 'Nom' },
              { key: 'unite', label: 'Unité' },
            ]}
            data={slicedNatures}
            keyField="id"
            emptyMessage="Aucune nature"
            actions={(row) => (
              <TableActions
                onEdit={() => { setEditingNature(row); setNatureModalOpen(true); }}
                onDelete={() => { setToDelete(row); setConfirmType('nature'); setConfirmOpen(true); }}
              />
            )}
          />
        </ListWithPagination>
      </div>

      <Modal open={familleModalOpen} onClose={() => setFamilleModalOpen(false)} title={editingFamille ? 'Modifier la famille' : 'Nouvelle famille'}>
        <FamilleForm famille={editingFamille ?? undefined} type={editingFamille ? 'update' : 'create'} onClose={() => setFamilleModalOpen(false)} onSuccess={() => setFamilleModalOpen(false)} />
      </Modal>
      <Modal open={natureModalOpen} onClose={() => setNatureModalOpen(false)} title={editingNature ? 'Modifier la nature' : 'Nouvelle nature'}>
        <NatureForm nature={editingNature ?? undefined} type={editingNature ? 'update' : 'create'} onClose={() => setNatureModalOpen(false)} onSuccess={() => setNatureModalOpen(false)} />
      </Modal>
      <ConfirmModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} message="Voulez-vous supprimer cet élément ?" />
    </div>
  );
}
